import Search from "@/components/common/forms/Search"
import BaseButton from "@/components/common/Button"
import Flex from "@/components/common/Flex"
import { H6 } from "@/components/common/Typography"
import ManageListEntry from "@/components/pages/manage/ManageListEntry"
import { styled } from "@/styles/theme"
import { useDispatch } from "react-redux"
import { setIsSlideOpen } from "@/store/slices/uiSlice"
import { ManageListProps } from "./interface"
import { Reorder, useDragControls } from 'framer-motion'
import useDragReordering from '@/hooks/useDragReordering'
import utilStyles from '@/styles/UtilityStyles.module.scss'
import { useEffectOnce } from 'react-use'
import { useCallback, useEffect, useRef, useState } from 'react'
import useIsTouchable from '@/hooks/useIsTouchable'
import { LinearProgress } from '@material-ui/core'
import { isDeepEqual } from '@/utils/util-functions'

const StyledButton = styled(BaseButton, {
  marginLeft: 'auto',
  display: 'block',
})

const StyledH6 = styled(H6, {
  marginBottom: 8,
  fontSize: 24,
})

const StyledFlex = styled(Flex, {
  display: 'flex',
  gap: '2rem',
  alignItems: 'center',
  marginBottom: '1.5rem',
})

const StyledCountContainer = styled('div', {
  flex: '1 0 auto',
  display: 'flex',
  alignItems: 'center',
  color: '$gray-dark',
  marginBottom: 0,
})

const StyledList = styled('ul', {
  listStyleType: 'none',
  paddingLeft: 0,
  marginBottom: '$8',
  borderBottom: '1px solid $colors$gray-light',
})

export default function ManageListCard({
  config,
  editableListData,
  actions,
  onCreateClick,
  onFeatureBlocked,
}: ManageListProps) {
  const dispatch = useDispatch()
  const { title, type, editPageSubtitle, isFeatureBlocked } = config

  const count = editableListData?.length || 0

  const {
    onReorder,
    setDragItems,
    items,
    isReordering,
    isDraggable,
    canDrag,
    setCanDrag,
  } = useDragReordering()

  const isTouchable = useIsTouchable()
  // TODO: timeout and duration can be removed if we don't need long press
  const timeout = useRef(null)
  const duration = 300

  const dragReorderHandler = useCallback(
    (data) => {
      if (isDraggable && !isReordering) {
        setDragItems(data)
      }
    },
    [setDragItems, isReordering, isDraggable]
  )

  const dragEndHandler = useCallback(
    (e) => {
      const target = e.target as Element
      if (
        isDraggable &&
        !isReordering &&
        target &&
        !target.className.includes('entry-action')
      ) {
        onReorder()
      }
    },
    [onReorder, isReordering, isDraggable]
  )

  useEffect(() => {
    setDragItems(editableListData)
  }, [])

  useEffect(() => {
    if (items?.length !== editableListData?.length) {
      setDragItems(editableListData)
    }
  }, [items, editableListData])

  return (
    <div
      style={{
        flex: 1,
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <StyledH6>{title || `${type}s`}</StyledH6>
        <p>{editPageSubtitle}</p>
      </header>

      <StyledFlex>
        <StyledCountContainer>
          {count} {type}
          {count !== 1 && 's'}
        </StyledCountContainer>
      </StyledFlex>
      {isReordering && <LinearProgress />}
      <div className="mb-4">
        {items && (
          <Reorder.Group
            values={items}
            axis="y"
            onReorder={(data) => {
              dragReorderHandler(data)
            }}
            onMouseUp={dragEndHandler}
            onTouchEnd={(e) => {
              dragEndHandler(e)
            }}
          >
            {items?.map((item, idx) => {
              // handle last of type styling while items are reordering
              const lastItemClasses =
                idx === editableListData.length - 1
                  ? utilStyles['borderBottomNone']
                  : null
              return (
                <ManageListEntry
                  actions={actions}
                  key={item.id}
                  item={item}
                  classes={lastItemClasses}
                />
              )
            })}
          </Reorder.Group>
        )}
      </div>

      <StyledButton
        onClick={() => {
          if (isFeatureBlocked && typeof onFeatureBlocked === 'function') {
            onFeatureBlocked()
            return
          }
          typeof onCreateClick === 'function' && onCreateClick()
          dispatch(setIsSlideOpen(true))
        }}
      >
        {`New ${type}`}
      </StyledButton>
    </div>
  )
}
